<template>
  <div class="data-panel">

    <!-- Controls bar -->
    <div class="logs-controls">
      <div class="logs-type-btns">
        <button
          class="log-type-btn"
          :class="{ 'log-type-btn--active': activeLog === 'actions' }"
          type="button"
          @click="activeLog = 'actions'"
        >Log de Ações</button>
        <button
          class="log-type-btn"
          :class="{ 'log-type-btn--active': activeLog === 'login' }"
          type="button"
          @click="activeLog = 'login'"
        >Login Logs</button>
      </div>

      <div class="logs-page-size">
        <label for="logs-per-page" class="logs-per-page-label">Por página:</label>
        <select id="logs-per-page" class="logs-per-page-select" :value="pageSize" @change="onPageSizeChange">
          <option v-for="n in pageSizeOptions" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
    </div>

    <!-- ── Log de Ações ── -->
    <template v-if="activeLog === 'actions'">
      <div v-if="logsLoading" class="logs-empty">Carregando...</div>
      <div v-else-if="logs.length === 0" class="logs-empty muted">Nenhum log registrado.</div>
      <template v-else>
        <div class="log-scroll">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Admin</th>
                <th>Ação</th>
                <th>Campo</th>
                <th>Antes</th>
                <th>Depois</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in pagedLogs" :key="log.id">
                <td class="td-nowrap">{{ formatDateTime(log.createdAt) }}</td>
                <td><code>{{ log.masterAdminUsername }}</code></td>
                <td>{{ log.action }}</td>
                <td>{{ log.fieldChanged || '—' }}</td>
                <td class="log-val">{{ log.valueBefore || '—' }}</td>
                <td class="log-val">{{ log.valueAfter || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="logs-footer">
          <span class="logs-count">{{ logs.length }} registro{{ logs.length !== 1 ? 's' : '' }}</span>
          <div class="paginator" v-if="totalLogPages > 1">
            <button class="pag-btn" :disabled="logsPage === 1" @click="logsPage = 1" type="button">«</button>
            <button class="pag-btn" :disabled="logsPage === 1" @click="logsPage--" type="button">‹</button>
            <button
              v-for="(p, i) in logPageNumbers"
              :key="i"
              class="pag-btn"
              :class="{ 'pag-btn--active': p === logsPage, 'pag-ellipsis': p === '…' }"
              :disabled="p === '…'"
              type="button"
              @click="p !== '…' && (logsPage = Number(p))"
            >{{ p }}</button>
            <button class="pag-btn" :disabled="logsPage === totalLogPages" @click="logsPage++" type="button">›</button>
            <button class="pag-btn" :disabled="logsPage === totalLogPages" @click="logsPage = totalLogPages" type="button">»</button>
          </div>
        </div>
      </template>
    </template>

    <!-- ── Login Logs ── -->
    <template v-if="activeLog === 'login'">
      <div v-if="loginLogsLoading" class="logs-empty">Carregando...</div>
      <div v-else-if="loginLogs.length === 0" class="logs-empty muted">Nenhum login registrado.</div>
      <template v-else>
        <div class="log-scroll">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Usuário</th>
                <th>IP</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ll in pagedLoginLogs" :key="ll.id">
                <td class="td-nowrap">{{ formatDateTime(ll.createdAt) }}</td>
                <td><code>{{ ll.username }}</code></td>
                <td>{{ ll.ip || '—' }}</td>
                <td><span :class="ll.success ? 'badge-ok' : 'badge-fail'">{{ ll.success ? 'Sucesso' : 'Falha' }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="logs-footer">
          <span class="logs-count">{{ loginLogs.length }} registro{{ loginLogs.length !== 1 ? 's' : '' }}</span>
          <div class="paginator" v-if="totalLoginPages > 1">
            <button class="pag-btn" :disabled="loginPage === 1" @click="loginPage = 1" type="button">«</button>
            <button class="pag-btn" :disabled="loginPage === 1" @click="loginPage--" type="button">‹</button>
            <button
              v-for="(p, i) in loginPageNumbers"
              :key="i"
              class="pag-btn"
              :class="{ 'pag-btn--active': p === loginPage, 'pag-ellipsis': p === '…' }"
              :disabled="p === '…'"
              type="button"
              @click="p !== '…' && (loginPage = Number(p))"
            >{{ p }}</button>
            <button class="pag-btn" :disabled="loginPage === totalLoginPages" @click="loginPage++" type="button">›</button>
            <button class="pag-btn" :disabled="loginPage === totalLoginPages" @click="loginPage = totalLoginPages" type="button">»</button>
          </div>
        </div>
      </template>
    </template>

  </div>
</template>

<script lang="ts">
import { ref, inject, computed, onMounted, watch } from 'vue'
import { masterFetch } from '@/store/masterSession'

interface AdminLog {
  id: string
  masterAdminUsername: string
  action: string
  fieldChanged: string
  valueBefore: string
  valueAfter: string
  createdAt: string
}

interface LoginLog {
  id: string
  username: string
  ip: string
  success: number
  createdAt: string
}

type PageNum = number | '…'

function buildPageNumbers(current: number, total: number): PageNum[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages: PageNum[] = []
  const push = (n: PageNum) => { if (pages[pages.length - 1] !== n) pages.push(n) }
  push(1)
  if (current > 3) push('…')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) push(i)
  if (current < total - 2) push('…')
  push(total)
  return pages
}

export default {
  name: 'AdminSysLogsTab',
  setup() {
    const detail = inject<any>('adminDetail')

    const activeLog = ref<'actions' | 'login'>('actions')
    const pageSizeOptions = [10, 25, 50, 100]
    const pageSize = ref(25)
    const logsPage = ref(1)
    const loginPage = ref(1)

    const logs = ref<AdminLog[]>([])
    const logsLoading = ref(false)
    const loginLogs = ref<LoginLog[]>([])
    const loginLogsLoading = ref(false)

    const onPageSizeChange = (e: Event) => {
      pageSize.value = Number((e.target as HTMLSelectElement).value)
      logsPage.value = 1
      loginPage.value = 1
    }

    const totalLogPages = computed(() => Math.max(1, Math.ceil(logs.value.length / pageSize.value)))
    const totalLoginPages = computed(() => Math.max(1, Math.ceil(loginLogs.value.length / pageSize.value)))

    const pagedLogs = computed(() => {
      const start = (logsPage.value - 1) * pageSize.value
      return logs.value.slice(start, start + pageSize.value)
    })
    const pagedLoginLogs = computed(() => {
      const start = (loginPage.value - 1) * pageSize.value
      return loginLogs.value.slice(start, start + pageSize.value)
    })

    const logPageNumbers = computed(() => buildPageNumbers(logsPage.value, totalLogPages.value))
    const loginPageNumbers = computed(() => buildPageNumbers(loginPage.value, totalLoginPages.value))

    watch(totalLogPages, (t) => { if (logsPage.value > t) logsPage.value = t })
    watch(totalLoginPages, (t) => { if (loginPage.value > t) loginPage.value = t })

    const loadLogs = async () => {
      if (!detail?.value) return
      logsLoading.value = true
      loginLogsLoading.value = true
      try {
        logs.value = await masterFetch<AdminLog[]>(`/master/systems/${detail.value.systemId}/logs`)
      } catch { /* ignore */ } finally { logsLoading.value = false }
      try {
        loginLogs.value = await masterFetch<LoginLog[]>(`/master/systems/${detail.value.systemId}/login-logs`)
      } catch { /* ignore */ } finally { loginLogsLoading.value = false }
    }

    onMounted(loadLogs)

    const formatDateTime = (d: string) => {
      if (!d) return ''
      try { return new Date(d).toLocaleString('pt-BR') } catch { return d }
    }

    return {
      activeLog,
      logs, logsLoading, loginLogs, loginLogsLoading,
      pageSizeOptions, pageSize, onPageSizeChange,
      logsPage, loginPage,
      pagedLogs, pagedLoginLogs,
      totalLogPages, totalLoginPages,
      logPageNumbers, loginPageNumbers,
      formatDateTime
    }
  }
}
</script>

<style lang="scss">
.logs-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.logs-type-btns {
  display: flex;
  gap: 0;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
}

.log-type-btn {
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;

  & + & { border-left: 1.5px solid #d1d5db; }
  &:hover:not(.log-type-btn--active) { background: #f3f4f6; color: #374151; }
  &.log-type-btn--active { background: #0f4c81; color: #fff; }
}

.logs-page-size {
  display: flex;
  align-items: center;
  gap: 8px;
}

.logs-per-page-label {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

.logs-per-page-select {
  padding: 5px 28px 5px 10px;
  font-size: 13px;
  border: 1.5px solid #d1d5db;
  border-radius: 6px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 8px center;
  -webkit-appearance: none;
  appearance: none;
  color: #374151;
  cursor: pointer;

  &:focus { outline: none; border-color: #0f4c81; }
}

.logs-empty { font-size: 14px; color: #6b7280; margin-bottom: 12px; }
.logs-empty.muted { color: #9ca3af; }

.log-scroll { overflow-x: auto; }

.log-val {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: monospace;
  font-size: 12px;
}

.td-nowrap { white-space: nowrap; }

.logs-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0 4px;
  flex-wrap: wrap;
  gap: 8px;
}

.logs-count { font-size: 13px; color: #9ca3af; }

.paginator {
  display: flex;
  gap: 3px;
  align-items: center;
  flex-wrap: wrap;
}

.pag-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &.pag-btn--active { background: #0f4c81; border-color: #0f4c81; color: #fff; font-weight: 700; }
  &.pag-ellipsis { border-color: transparent; background: transparent; cursor: default; }
}
</style>
